import type { DocumentNode } from 'graphql';
import type { GetStaticPaths } from 'next';
import Image from 'next/image';
import { useRouter } from 'next/router';
import type { ParsedUrlQuery } from 'querystring';
import { useCallback, useMemo } from 'react';
import type { MessageDescriptor } from 'react-intl';
import { defineMessages, FormattedMessage } from 'react-intl';

import { optiDigitalCancelInfiniteLoad } from '../../../front/src/components/Ads/OptiDigitalAds';
import { generateOptiAds, ultimediaElement } from '../../../front/src/components/Ads/PageListAds';
import { AvoidHeaderAnchor } from '../../../front/src/components/AvoidHeaderAnchor';
import { ErrorBlock } from '../../../front/src/components/ErrorBlock';
import { Pagination } from '../../../front/src/components/Pagination/Pagination';
import { keyPeriodsGetter } from '../../../front/src/components/PeriodMenu';
import { QUERY_PAGE_NAME } from '../../../front/src/constants/message';
import { LayoutBlocks } from '../../../front/src/contributor/Layout/AreaBlocks';
import AdultConsent from '../../../front/src/contributor/Profile/AdultConsent';
import { useDataExposer } from '../../../front/src/hooks/useExposedData';
import { useQueryCallback } from '../../../front/src/hooks/useQueryCallback';
import { useQueryNavigation } from '../../../front/src/hooks/useQueryNavigation';
import { getPageFromQuery, useQueryPage } from '../../../front/src/hooks/useQueryPage';
import { useSSREffect } from '../../../front/src/hooks/useSSREffect';
import { useWebsite } from '../../../front/src/hooks/useWebsite';
import { useAppDispatch, useAppSelector } from '../../../front/src/redux/hooks';
import { selectCustomSearchDensity } from '../../../front/src/redux/slices/utils-slice';
import { addApolloState } from '../../../front/src/services/apollo/createApolloClient';
import {
  selectTagInfo,
  selectTagTopicList,
  setTagInfo,
} from '../../../front/src/thread/ThreadMessage/topics-slice';
import type { ThreadMessagesSortType } from '../../../front/src/thread/ThreadMessages/query';
import { threadMessagesSortTypeValue } from '../../../front/src/thread/ThreadMessages/query';
import ContentHeader from '../../../front/src/thread/Topic/ContentHeader';
import { TopicList } from '../../../front/src/thread/Topic/TopicList';
import type { Category, Graphdebate, QueryType, Topic } from '../../../front/src/types/api';
import type { PageType } from '../../../front/src/types/layout';
import iconEmptyStreet from '../../public/illustrations/icon-empty-street.svg';
import { useHasAds } from '../utils/common';
import type { GetStaticPropsGD } from '../utils/getStaticPropsCommon';
import { getStaticPropsCommon } from '../utils/getStaticPropsCommon';
import { checkWebsiteIsVIP } from '../utils/middleware-utils';
import { mkTmpApolloClient } from '../utils/mkTmpApolloClient';
import { useOnCachedTokenReady } from './auth/HasTokenProvider';
import { MetaPage } from './Meta/MetaPage';

// B4_05 — page de navigation par facette (thème ou entité). Quasi-clone de la page de
// tag `[website]/[tag].tsx`, factorisé pour les deux routes /theme/<slug> et /entity/<slug>.
// La query passée alias `facetLoad` en `categoryLoad`, ce qui permet de réutiliser tel quel
// le redux topics-slice (`setTagInfo` lit `website.categoryLoad`) et tout le rendu existant.

export type FacetType = 'theme' | 'entity';

const FACET_DEFAULT_SORT: ThreadMessagesSortType = 'newest';

const TOP_LIST_ANCHOR = '__gc__top__list__facet__';

const messages = defineMessages({
  empty: { defaultMessage: 'This tag is sad without a topic.', id: 'gi4OAu' },
  errorPeriod: { defaultMessage: 'The searched period does not exist', id: 'MnTYre' },
  errorPage: { defaultMessage: 'The searched page does not exist', id: 'TQfq3N' },
  errorSlug: { defaultMessage: 'The searched tag does not exist', id: 'cQ7Ybb' },
});

const computePublicData = (tag: Category) => {
  if (!tag) return null;

  return {
    pageName: 'tag',
    title: tag.title,
    tag,
  };
};

type PeriodIndex = keyof typeof keyPeriodsGetter;

function makeFacetQueryVariables(query: ParsedUrlQuery, graphdebate: Graphdebate) {
  const page = getPageFromQuery(query);
  const { countPerListPage = 10 } = graphdebate;
  const period = query['period'] as string;
  const rawSort = query['sort'] as string;
  const { slug } = query;
  const [dateStart = null, dateEnd = null] = keyPeriodsGetter[period as PeriodIndex]
    ? keyPeriodsGetter[period as PeriodIndex]()
    : [];
  const isValidSort = threadMessagesSortTypeValue.includes(rawSort as any);
  const sort = (isValidSort ? rawSort : FACET_DEFAULT_SORT) as ThreadMessagesSortType;
  return {
    page,
    limit: countPerListPage,
    dateStart,
    dateEnd,
    sort,
    slug,
  };
}

export interface FacetPageProps {
  facetType: FacetType;
  query: DocumentNode;
  statusMsg?: string | MessageDescriptor;
  initialData: QueryType | null;
  // contentAreaLayout n'est PAS déclaré ici : il provient de DebateAlwaysProps (injecté
  // par PageType<P> = FC<DebateAlwaysProps & P>), typé LayoutBlock[].
}

export const FacetTopicsPage: PageType<FacetPageProps> = props => {
  const {
    facetType,
    query: facetQuery,
    statusMsg,
    initialData: initialDataProp,
    contentAreaLayout,
  } = props;

  const { graphdebate = {} } = useWebsite();
  const { query } = useRouter();

  const { countPerListPage = 10 } = graphdebate;
  const { slug } = query;

  // Mode presse (média) : pages facettes alignées sur les colonnes de la home magazine
  // (max-w-5xl — B11_02, même règle que la page tag). Gaté `mode==='media'` → forum inchangé.
  const isMediaMode = graphdebate?.mode === 'media';

  // sort
  const { value: rawSort } = useQueryNavigation('sort');

  const isValidSort = threadMessagesSortTypeValue.includes(rawSort as any);
  const sort = useMemo(
    () => (isValidSort ? rawSort : FACET_DEFAULT_SORT) as ThreadMessagesSortType,
    [rawSort, isValidSort],
  );

  // period
  const { value: period, update: changePeriod } = useQueryNavigation('period');

  // density
  const densityToDisplay = useAppSelector(selectCustomSearchDensity) || 'standard';

  // page
  const page = useQueryPage();

  const variables = makeFacetQueryVariables(query, graphdebate);

  const dispatch = useAppDispatch();
  const updateReduxState = useCallback(
    (_data: QueryType) => {
      dispatch(setTagInfo(_data.website));
    },
    [dispatch],
  );

  const { error, loading, initialData, refetch } = useQueryCallback({
    query: facetQuery,
    variables,
    initialData: initialDataProp,
    onResponse: updateReduxState,
  });

  useOnCachedTokenReady(hasToken => {
    if (hasToken) {
      refetch();
    }
  });

  const tag = useAppSelector(selectTagInfo) || initialData?.website?.categoryLoad;
  const topicList = useAppSelector(selectTagTopicList) || initialData?.website?.topicList;

  const dataExposer = useDataExposer();
  const topics = useMemo(
    () => (topicList?.topics?.filter(t => t) as Topic[]) ?? [],
    [topicList?.topics],
  );

  useSSREffect(() => {
    const publicData = computePublicData(tag as Category);

    if (!publicData) return;

    optiDigitalCancelInfiniteLoad();
    dataExposer.setDataExposed(publicData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tag?.id, dataExposer]);

  const metaData = useMemo(
    () => ({
      tagName: tag?.slug,
      tagTitle: tag?.title,
      tagDescription: tag?.description,
    }),
    [tag],
  );

  const hasAds = useHasAds();

  const computedAds = useMemo(
    () =>
      !hasAds
        ? undefined
        : [
            {
              position: 2,
              element: ultimediaElement,
            },
            ...generateOptiAds({
              start: 5,
              freq: 7,
              isMobile: false,
              total: countPerListPage,
            }),
            ...generateOptiAds({
              start: 5,
              freq: 5,
              isMobile: true,
              total: countPerListPage,
            }),
          ],
    [countPerListPage, hasAds],
  );

  const metaElement = useMemo(
    () => (
      <MetaPage
        title="{tagName} - {websiteName}"
        canonicalPath={`/${facetType}/${slug}`}
        data={metaData as Record<string, string>}
        index
        follow
      />
    ),
    [facetType, slug, metaData],
  );

  if (!tag) {
    return <>{metaElement}</>;
  }

  if (typeof statusMsg === 'string')
    return (
      <>
        {metaElement}
        {statusMsg}
      </>
    );

  if (typeof statusMsg === 'object')
    return (
      <>
        {metaElement}
        <FormattedMessage {...statusMsg} />
      </>
    );

  if (error && !loading) {
    return (
      <>
        {metaElement}
        <ErrorBlock error={error} />;
      </>
    );
  }

  return (
    <div className={`flex flex-col space-y-5${isMediaMode ? ' max-w-5xl w-full mx-auto' : ''}`}>
      {metaElement}
      {contentAreaLayout && <LayoutBlocks layoutContent={contentAreaLayout} />}
      <AvoidHeaderAnchor anchorName={TOP_LIST_ANCHOR} />

      <ContentHeader
        contentTitle={tag?.title || tag?.slug}
        listLength={topicList?.meta?.count ?? 0}
        topicListActions={{
          sortMenu: { sortType: sort },
          periodMenu: { period, changePeriod },
          densityMenu: { densityType: densityToDisplay, listType: 'searchTopics' },
          createTopicCTA: true,
        }}
        withCategoriesMenu
      />

      {tag.description && (
        <div className="">
          <p>{tag.description}</p>
        </div>
      )}

      {topics.length === 0 && !loading && (
        <div className="rounded-lg mt-5">
          <div className="w-full md:w-2/3 mx-auto">
            <Image
              src={iconEmptyStreet}
              alt="icon-empty-street.svg"
              className="w-full"
              // Dummy props for nextjs to accept it. The SVG is sized by the CSS classes above.
              width={100}
              height={100}
            />
            <div className="my-5 p-5 bg-primary-base/10 text-primary-base text-center rounded-lg">
              <FormattedMessage {...messages.empty} />
            </div>
          </div>
        </div>
      )}

      <TopicList
        topics={topics}
        computedAds={computedAds}
        density={densityToDisplay}
        withActions
        bottomBorder
      />

      {hasAds && topics.length < 2 && <div className="pt-3">{ultimediaElement}</div>}

      <div className="mt-5">
        <Pagination
          count={topicList?.meta?.count ?? 0}
          limit={countPerListPage}
          page={page}
          queryName={QUERY_PAGE_NAME}
          anchor={TOP_LIST_ANCHOR}
          loading={loading}
        />
      </div>

      {tag.adult && <AdultConsent tag={tag} />}
    </div>
  );
};

// Factory de getStaticProps partagée par les deux routes facette. Clone fidèle de la
// page de tag : SSR de la query, garde wrongSlug (slug inexistant → 404, décision UX
// Mathias 2026-06-24), wrongPage, addApolloState. `Component` porte le pageName (theme/entity).
export function makeFacetGetStaticProps(
  Component: PageType<FacetPageProps>,
  facetQuery: DocumentNode,
): GetStaticPropsGD<Partial<FacetPageProps>> {
  return async context => {
    let { response, website } = await getStaticPropsCommon(context, Component);
    if (response && ('redirect' in response || 'notFound' in response)) {
      return response;
    }
    const apolloClient = mkTmpApolloClient(context);

    const routerQuery = (context.params || {}) as any;
    const graphdebate = website.graphdebate!;

    const { period, slug } = routerQuery;
    const page = getPageFromQuery(routerQuery);
    const [dateStart = null, dateEnd = null] = keyPeriodsGetter[period as PeriodIndex]
      ? keyPeriodsGetter[period as PeriodIndex]()
      : [];
    if ((dateStart === null || dateEnd === null) && period && period !== 'noPeriod') {
      return {
        notFound: true,
        props: {
          hideLayoutError: true,
          statusMsg: messages.errorPeriod,
        },
      };
    }

    const websiteIsVIP = checkWebsiteIsVIP(website);

    let data: QueryType | null = null;
    if (!websiteIsVIP) {
      data = (
        await apolloClient.query<QueryType>({
          query: facetQuery,
          variables: makeFacetQueryVariables(routerQuery, graphdebate),
          fetchPolicy: 'no-cache',
        })
      ).data;

      const count = Number(data?.website?.topicList?.meta?.count ?? 0);
      const wrongPage = page > 1 && count <= (page - 1) * 10;
      const wrongSlug = data?.website?.categoryLoad?.slug !== slug;

      if (wrongSlug) {
        return {
          notFound: true,
          props: {
            hideLayoutError: true,
            statusMsg: messages.errorSlug,
          },
        };
      }

      if (wrongPage) {
        return {
          notFound: true,
          props: {
            hideLayoutError: true,
            statusMsg: messages.errorPage,
          },
        };
      }
    }

    response.props = {
      ...response.props,
      pageConfig: { previousPage: 'categories', withSubHeader: true },
      initialData: data,
    };
    response = addApolloState(apolloClient, response);
    return response;
  };
}

export const facetStaticPaths: GetStaticPaths = async () => ({
  paths: [],
  fallback: 'blocking',
});
